import { concat, forEach, keys, set } from 'lodash';

import {
  getOrchestrationKey,
  Input,
  Inputs,
  ModelInference,
  OrchestrationInference,
  ResultNameOrchestrations,
  SlideInferenceResults,
} from 'interfaces/calculateFeatures';
import { ClassificationModel } from 'interfaces/classificationModel';
import { CalculateFeaturesParams } from 'interfaces/job';
import { CellRules, Thresholds } from 'interfaces/jobs/multiplex/cellTypingParams';
import { AssignmentByStain, SelectedAssignmentDetails } from 'utils/assignmentManagement';
import { ModelPreferencesById } from 'utils/models';
import { normalizeKeysToCamelCase } from 'utils/objectCase';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { RegistrationDetails, SlideRegistrationDetailsByOrchestration } from 'utils/slideRegistrations';
import { filterInputsBySlideIds, getArtifactOrchestrationId, getPipsStudyInputsState } from './calculateFeaturesInputs';
import { resolveGenericResultBinningPreference, resolveInferenceInputModelPreference } from './modelBinningPreferences';
import { resolveSpotDetectionSetupPreference } from './spotDetectionInputSetup';
import { findByOrchestrationResultArtifactUrlPattern, findOrchestrationByArtifactUrlPattern } from './utils';

export interface PreviousJobSelectedResultsSnapshot {
  selectedModelOrchestrations: OrchestrationBySlideByType;
  selectedPostprocessedOrchestrations: OrchestrationBySlideByResultName;
  selectedPipsOrchestrations: OrchestrationBySlideByResultName;
  selectedPipsAreaInstancesOrchestrations: OrchestrationBySlideByResultName;
  selectedCellQuantificationOrchestrations: OrchestrationBySlideByResultName;
  selectedAreaQuantificationOrchestrations: OrchestrationBySlideByResultName;
  selectedSlideSegmentationsTableOrchestrations: OrchestrationBySlideByResultName;
  selectedSlideDetectionsTableOrchestrations: OrchestrationBySlideByResultName;
  selectedAssignments: AssignmentByStain;
  modelPreferencesByModelId: ModelPreferencesById;
}

export interface PreviousJobGeneralInputsSnapshot {
  perMarkerThresholds: Thresholds | null;
  perMarkerThresholdsOrchestrationId: string | null;
  cellRules: CellRules | null;
  knnOrchestrationId: string | null;
  neighborhoodClustersOrchestrationId: string | null;
}

export const emptyPreviousJobSelectedResultsSnapshot: PreviousJobSelectedResultsSnapshot = {
  selectedModelOrchestrations: {},
  selectedPostprocessedOrchestrations: {},
  selectedPipsOrchestrations: {},
  selectedPipsAreaInstancesOrchestrations: {},
  selectedCellQuantificationOrchestrations: {},
  selectedAreaQuantificationOrchestrations: {},
  selectedSlideSegmentationsTableOrchestrations: {},
  selectedSlideDetectionsTableOrchestrations: {},
  selectedAssignments: {},
  modelPreferencesByModelId: {},
};

export const emptyPreviousJobGeneralInputsSnapshot: PreviousJobGeneralInputsSnapshot = {
  perMarkerThresholds: null,
  perMarkerThresholdsOrchestrationId: null,
  cellRules: null,
  knnOrchestrationId: null,
  neighborhoodClustersOrchestrationId: null,
};

export const buildPreviousJobSelectedResultsSnapshot = (params: PreviousJobSelectedResultsSnapshot) => ({
  selectedModelOrchestrations: params.selectedModelOrchestrations,
  selectedPostprocessedOrchestrations: params.selectedPostprocessedOrchestrations,
  selectedPipsOrchestrations: params.selectedPipsOrchestrations,
  selectedPipsAreaInstancesOrchestrations: params.selectedPipsAreaInstancesOrchestrations,
  selectedCellQuantificationOrchestrations: params.selectedCellQuantificationOrchestrations,
  selectedAreaQuantificationOrchestrations: params.selectedAreaQuantificationOrchestrations,
  selectedSlideSegmentationsTableOrchestrations: params.selectedSlideSegmentationsTableOrchestrations,
  selectedSlideDetectionsTableOrchestrations: params.selectedSlideDetectionsTableOrchestrations,
  selectedAssignments: params.selectedAssignments,
  modelPreferencesByModelId: params.modelPreferencesByModelId,
});

export const buildPreviousJobGeneralInputsSnapshot = (params: PreviousJobGeneralInputsSnapshot) => ({
  perMarkerThresholds: params.perMarkerThresholds,
  perMarkerThresholdsOrchestrationId: params.perMarkerThresholdsOrchestrationId,
  cellRules: params.cellRules,
  knnOrchestrationId: params.knnOrchestrationId,
  neighborhoodClustersOrchestrationId: params.neighborhoodClustersOrchestrationId,
});

export const buildPreviousJobGeneralInputsSnapshotFromPipsStudyInputs = (
  pipsStudyInputs?: CalculateFeaturesParams['pipsStudyInputs'] | null
) => {
  const pipsStudyInputsState = getPipsStudyInputsState(pipsStudyInputs);

  return buildPreviousJobGeneralInputsSnapshot({
    perMarkerThresholds: pipsStudyInputsState.thresholds ?? null,
    perMarkerThresholdsOrchestrationId: pipsStudyInputsState.perMarkerThresholdsOrchestrationId ?? null,
    cellRules: pipsStudyInputsState.cellRules ?? null,
    knnOrchestrationId: pipsStudyInputsState.knnOrchestrationId ?? null,
    neighborhoodClustersOrchestrationId: pipsStudyInputsState.neighborhoodClustersOrchestrationId ?? null,
  });
};

export const buildSelectedSlideRegistrationsByOrchestration = (
  registrations?: RegistrationDetails[] | null
): SlideRegistrationDetailsByOrchestration => {
  const registrationsByOrchestration: SlideRegistrationDetailsByOrchestration = {};

  forEach(registrations ?? [], (registration) => {
    const orchestrationId = getArtifactOrchestrationId(registration.artifactUrl);

    if (!orchestrationId) {
      return;
    }

    registrationsByOrchestration[orchestrationId] = concat(
      registrationsByOrchestration[orchestrationId] ?? [],
      registration
    );
  });

  return registrationsByOrchestration;
};

interface BuildPreviousJobSelectedResultsSnapshotFromInputsParams {
  params: { inputs?: Inputs };
  slidesById: Record<string, SlideInferenceResults>;
  allInferenceResults: ModelInference[];
  postprocessedArtifacts?: ResultNameOrchestrations[];
  pipsResultsArtifacts?: ResultNameOrchestrations[];
  pipsAreaInstancesArtifacts?: ResultNameOrchestrations[];
  cellQuantificationArtifacts?: ResultNameOrchestrations[];
  areaQuantificationArtifacts?: ResultNameOrchestrations[];
  slideSegmentationsTableArtifacts?: ResultNameOrchestrations[];
  slideDetectionsTableArtifacts?: ResultNameOrchestrations[];
  classificationModelOptions: ClassificationModel[];
  getNewSelectedAssignmentDetails: (
    selectedAssignmentDetails: AssignmentByStain,
    stainingType: string,
    result: Input
  ) => SelectedAssignmentDetails[];
}

export const buildPreviousJobSelectedResultsSnapshotFromInputs = ({
  params,
  slidesById,
  allInferenceResults,
  postprocessedArtifacts,
  pipsResultsArtifacts,
  pipsAreaInstancesArtifacts,
  cellQuantificationArtifacts,
  areaQuantificationArtifacts,
  slideSegmentationsTableArtifacts,
  slideDetectionsTableArtifacts,
  classificationModelOptions,
  getNewSelectedAssignmentDetails,
}: BuildPreviousJobSelectedResultsSnapshotFromInputsParams): PreviousJobSelectedResultsSnapshot => {
  const restoredSelectedResultsSnapshot = buildPreviousJobSelectedResultsSnapshot({
    ...emptyPreviousJobSelectedResultsSnapshot,
  });

  const filteredInputs = filterInputsBySlideIds(params.inputs ?? {}, keys(slidesById));

  forEach(filteredInputs, (results) => {
    forEach(results, (slideResults, slideId) => {
      const currentSlide = slidesById[slideId];
      if (!currentSlide) {
        console.warn(`Slide with id ${slideId} not found in slidesById, despite being in filtered inputs.`);
      }

      forEach(slideResults, (result) => {
        const normalizedInput = normalizeKeysToCamelCase(result?.input);
        const normalizedOverride = normalizeKeysToCamelCase(result?.override);

        if (normalizedInput?.artifactUrl) {
          const orchestrationResultArtifactUrlPattern = normalizedInput.artifactUrl;
          const resolveRestoredOrchestration = (
            artifactGroup: Pick<ResultNameOrchestrations, 'orchestrations'> | ModelInference | undefined,
            selectionType: string
          ): OrchestrationInference | null => {
            const currentOrchestration = findOrchestrationByArtifactUrlPattern<OrchestrationInference>(
              artifactGroup?.orchestrations,
              orchestrationResultArtifactUrlPattern
            );

            if (!currentOrchestration) {
              console.warn('Skipping restored selection because orchestration could not be resolved', {
                slideId,
                selectionType,
                orchestrationResultArtifactUrlPattern,
              });
              return null;
            }

            return currentOrchestration;
          };
          const resolveRestoredResultKey = (
            artifactGroup: ResultNameOrchestrations,
            selectionType: string
          ): string | null => {
            const resultKey = getOrchestrationKey(artifactGroup);

            if (!resultKey) {
              console.warn('Skipping restored selection because result key could not be resolved', {
                slideId,
                selectionType,
                orchestrationResultArtifactUrlPattern,
              });
              return null;
            }

            return resultKey;
          };
          const postprocessedResultName = findByOrchestrationResultArtifactUrlPattern<ResultNameOrchestrations>(
            postprocessedArtifacts ?? [],
            orchestrationResultArtifactUrlPattern
          );
          const pipsResultName = findByOrchestrationResultArtifactUrlPattern<ResultNameOrchestrations>(
            pipsResultsArtifacts ?? [],
            orchestrationResultArtifactUrlPattern
          );
          const pipsAreaInstancesOrchestrationResultName =
            findByOrchestrationResultArtifactUrlPattern<ResultNameOrchestrations>(
              pipsAreaInstancesArtifacts ?? [],
              orchestrationResultArtifactUrlPattern
            );
          const cellQuantificationResultName = findByOrchestrationResultArtifactUrlPattern<ResultNameOrchestrations>(
            cellQuantificationArtifacts ?? [],
            orchestrationResultArtifactUrlPattern
          );
          const areaQuantificationResultName = findByOrchestrationResultArtifactUrlPattern<ResultNameOrchestrations>(
            areaQuantificationArtifacts ?? [],
            orchestrationResultArtifactUrlPattern
          );
          const slideSegmentationsTableResultName =
            findByOrchestrationResultArtifactUrlPattern<ResultNameOrchestrations>(
              slideSegmentationsTableArtifacts ?? [],
              orchestrationResultArtifactUrlPattern
            );
          const slideDetectionsTableResultName = findByOrchestrationResultArtifactUrlPattern<ResultNameOrchestrations>(
            slideDetectionsTableArtifacts ?? [],
            orchestrationResultArtifactUrlPattern
          );

          const currentModel = findByOrchestrationResultArtifactUrlPattern<ModelInference>(
            allInferenceResults,
            orchestrationResultArtifactUrlPattern
          );
          if (postprocessedResultName) {
            const currentOrchestration = resolveRestoredOrchestration(postprocessedResultName, 'postprocessed');
            const resultKey = resolveRestoredResultKey(postprocessedResultName, 'postprocessed');

            if (currentOrchestration && resultKey) {
              restoredSelectedResultsSnapshot.selectedPostprocessedOrchestrations[slideId] = {
                ...restoredSelectedResultsSnapshot.selectedPostprocessedOrchestrations[slideId],
                [resultKey]: { orchestration: currentOrchestration },
              };
            }
          } else if (pipsResultName) {
            const currentOrchestration = resolveRestoredOrchestration(pipsResultName, 'pips');
            const resultKey = resolveRestoredResultKey(pipsResultName, 'pips');

            if (currentOrchestration && resultKey) {
              restoredSelectedResultsSnapshot.selectedPipsOrchestrations[slideId] = {
                ...restoredSelectedResultsSnapshot.selectedPipsOrchestrations[slideId],
                [resultKey]: { orchestration: currentOrchestration },
              };
            }
          } else if (pipsAreaInstancesOrchestrationResultName) {
            const currentOrchestration = resolveRestoredOrchestration(
              pipsAreaInstancesOrchestrationResultName,
              'pips-area-instances'
            );
            const resultKey = resolveRestoredResultKey(pipsAreaInstancesOrchestrationResultName, 'pips-area-instances');

            if (currentOrchestration && resultKey) {
              restoredSelectedResultsSnapshot.selectedPipsAreaInstancesOrchestrations[slideId] = {
                ...restoredSelectedResultsSnapshot.selectedPipsAreaInstancesOrchestrations[slideId],
                [resultKey]: { orchestration: currentOrchestration },
              };
            }
          } else if (cellQuantificationResultName) {
            const currentOrchestration = resolveRestoredOrchestration(
              cellQuantificationResultName,
              'cell-quantification'
            );
            const resultKey = resolveRestoredResultKey(cellQuantificationResultName, 'cell-quantification');

            if (currentOrchestration && resultKey) {
              restoredSelectedResultsSnapshot.selectedCellQuantificationOrchestrations[slideId] = {
                ...restoredSelectedResultsSnapshot.selectedCellQuantificationOrchestrations[slideId],
                [resultKey]: {
                  orchestration: currentOrchestration,
                },
              };

              const resolvedPreference = resolveGenericResultBinningPreference({
                resultKey,
                resultInput: normalizedInput,
              });
              if (resolvedPreference) {
                restoredSelectedResultsSnapshot.modelPreferencesByModelId[resolvedPreference.preferenceKey] =
                  resolvedPreference.preferenceValue;
              }
            }
          } else if (areaQuantificationResultName) {
            const currentOrchestration = resolveRestoredOrchestration(
              areaQuantificationResultName,
              'area-quantification'
            );
            const resultKey = resolveRestoredResultKey(areaQuantificationResultName, 'area-quantification');

            if (currentOrchestration && resultKey) {
              restoredSelectedResultsSnapshot.selectedAreaQuantificationOrchestrations[slideId] = {
                ...restoredSelectedResultsSnapshot.selectedAreaQuantificationOrchestrations[slideId],
                [resultKey]: {
                  orchestration: currentOrchestration,
                },
              };

              const resolvedPreference = resolveGenericResultBinningPreference({
                resultKey,
                resultInput: normalizedInput,
              });
              if (resolvedPreference) {
                restoredSelectedResultsSnapshot.modelPreferencesByModelId[resolvedPreference.preferenceKey] =
                  resolvedPreference.preferenceValue;
              }
            }
          } else if (slideSegmentationsTableResultName) {
            const currentOrchestration = resolveRestoredOrchestration(
              slideSegmentationsTableResultName,
              'slide-segmentations-table'
            );
            const resultKey = resolveRestoredResultKey(slideSegmentationsTableResultName, 'slide-segmentations-table');

            if (currentOrchestration && resultKey) {
              restoredSelectedResultsSnapshot.selectedSlideSegmentationsTableOrchestrations[slideId] = {
                ...restoredSelectedResultsSnapshot.selectedSlideSegmentationsTableOrchestrations[slideId],
                [resultKey]: {
                  orchestration: currentOrchestration,
                },
              };
            }
          } else if (slideDetectionsTableResultName) {
            const currentOrchestration = resolveRestoredOrchestration(
              slideDetectionsTableResultName,
              'slide-detections-table'
            );
            const resultKey = resolveRestoredResultKey(slideDetectionsTableResultName, 'slide-detections-table');

            if (currentOrchestration && resultKey) {
              restoredSelectedResultsSnapshot.selectedSlideDetectionsTableOrchestrations[slideId] = {
                ...restoredSelectedResultsSnapshot.selectedSlideDetectionsTableOrchestrations[slideId],
                [resultKey]: {
                  orchestration: currentOrchestration,
                },
              };

              const resolvedPreference = resolveSpotDetectionSetupPreference({
                resultKey,
                resultInput: normalizedInput,
              });
              if (resolvedPreference) {
                restoredSelectedResultsSnapshot.modelPreferencesByModelId[resolvedPreference.preferenceKey] = {
                  ...restoredSelectedResultsSnapshot.modelPreferencesByModelId[resolvedPreference.preferenceKey],
                  ...resolvedPreference.preferenceValue,
                };
                // Per-probe overrides live under their own probe-keyed preference entry, mirroring how
                // they're read elsewhere (see getSpotDetectionThresholdOptions and friends).
                forEach(resolvedPreference.perProbeCountBins, (countBins, probeName) => {
                  restoredSelectedResultsSnapshot.modelPreferencesByModelId[probeName] = {
                    ...restoredSelectedResultsSnapshot.modelPreferencesByModelId[probeName],
                    countBins,
                  };
                });
              }
            }
          } else if (currentModel) {
            const currentOrchestration = resolveRestoredOrchestration(currentModel, currentModel.modelType);

            if (currentOrchestration) {
              restoredSelectedResultsSnapshot.selectedModelOrchestrations[slideId] = {
                ...restoredSelectedResultsSnapshot.selectedModelOrchestrations[slideId],
                [normalizedInput?.readAs ?? currentModel.modelType]: {
                  orchestration: currentOrchestration,
                  model: currentModel,
                },
              };

              const resolvedPreference = resolveInferenceInputModelPreference({
                resultInput: normalizedInput,
                currentModel,
                classificationModelOptions,
              });
              if (resolvedPreference) {
                restoredSelectedResultsSnapshot.modelPreferencesByModelId[resolvedPreference.preferenceKey] =
                  resolvedPreference.preferenceValue;
              }
            }
          }
        } else if (normalizedInput.assignmentId) {
          set(
            restoredSelectedResultsSnapshot.selectedAssignments,
            currentSlide.stainingType,
            getNewSelectedAssignmentDetails(
              restoredSelectedResultsSnapshot.selectedAssignments,
              currentSlide.stainingType,
              normalizedInput
            )
          );
        }

        if (normalizedOverride?.assignmentId) {
          set(
            restoredSelectedResultsSnapshot.selectedAssignments,
            currentSlide.stainingType,
            getNewSelectedAssignmentDetails(
              restoredSelectedResultsSnapshot.selectedAssignments,
              currentSlide.stainingType,
              normalizedOverride
            )
          );
        }
      });
    });
  });

  return restoredSelectedResultsSnapshot;
};
